import React, { useState, useEffect } from "react"
import MetaTags from "react-meta-tags"
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input
} from "reactstrap"
import classnames from "classnames"
import Breadcrumbs from "components/Common/Breadcrumb"
import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import Flatpickr from "react-flatpickr"
import { get, post } from "helpers/api_helper"
import Notifications from "components/Notifications"
import { LEAVE_URL, LEAVE_TYPE_URL } from "helpers/url_helper"
import "flatpickr/dist/themes/material_green.css"

const Leaves = () => {
    const [activeTab, setActiveTab] = useState("2")
    const [leaveTypeModal, setLeaveTypeModal] = useState(false)
    const [leaveRequestModal, setLeaveRequestModal] = useState(false)

    // States for Leave Types
    const [leaveTypeData, setLeaveTypeData] = useState({ rows: [], total_rows: 0 })
    const [leaveTypeFilters, setLeaveTypeFilters] = useState({ branch: null, leaveType: "" })

    // States for Leave Requests
    const [leaveRequestData, setLeaveRequestData] = useState({ rows: [], total_rows: 0 })
    const [leaveRequestFilters, setLeaveRequestFilters] = useState({
        branch: null,
        department: null,
        employee: null,
        leaveType: null,
        year: { label: "2026", value: "2026" },
        month: null,
        status: null
    })

    const [pagination, setPagination] = useState({
        limit: 10,
        page: 1,
        search: ""
    })
    const [isLoading, setIsLoading] = useState(false)

    // Form States for Leave Types
    const [leaveTypeForm, setLeaveTypeForm] = useState({
        branch_id: "",
        leave_type_name: "",
        gender: "Both",
        is_paid_leave: 1,
        leave_allocated_days: 0
    })

    // Form States for Leave Requests
    const [leaveRequestForm, setLeaveRequestForm] = useState({
        branch_id: "",
        department_id: "",
        lab_department_id: "",
        employee_id: "",
        leave_type_id: "",
        from_date: "",
        to_date: "",
        reason: ""
    })

    const toggleTab = tab => {
        if (activeTab !== tab) setActiveTab(tab)
    }

    const toggleLeaveTypeModal = () => setLeaveTypeModal(!leaveTypeModal)
    const toggleLeaveRequestModal = () => setLeaveRequestModal(!leaveRequestModal)

    // Fetch Data from API
    const getData = async () => {
        setIsLoading(true)

        // Fetch Leave Requests (Tab 2)
        const res = await get(`${LEAVE_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.search}`);
        if (res.status === 200) {
            setLeaveRequestData({
                rows: res.response.rows || [],
                total_rows: res.response.total_rows || 0
            });
        }

        // Fetch Leave Types (Tab 1)
        const resTypes = await get(LEAVE_TYPE_URL);
        if (resTypes.status === 200) {
            setLeaveTypeData({
                rows: resTypes.response.rows || [],
                total_rows: resTypes.response.totalRows || 0
            });
        }

        setIsLoading(false)
    }


    const handleLeaveTypeSubmit = async (e) => {
        e.preventDefault()
        const res = await post(LEAVE_TYPE_URL, leaveTypeForm)
        if (res.status === 200) {
            Notifications("Leave Type added successfully!")
            toggleLeaveTypeModal()
            getData()
        }
    }

    const handleLeaveRequestSubmit = async (e) => {
        e.preventDefault()
        const res = await post(LEAVE_URL, leaveRequestForm)
        if (res.status === 200) {
            Notifications("Leave Request submitted successfully!")
            toggleLeaveRequestModal()
            getData()
        }
    }

    useEffect(() => {
        const handler = setTimeout(() => {
            getData();
        }, 300);
        return () => clearTimeout(handler);
    }, [pagination, activeTab]);

    const leaveTypeColumns = [
        { dataField: "id", text: "#" },
        { dataField: "leave_type_name", text: "TYPE" },
        { dataField: "is_paid_leave", text: "IS PAID ?", formatter: (cell) => cell ? "Yes" : "No" },
        { dataField: "leave_allocated_days", text: "ALLOCATED DAYS" },
        {
            dataField: "status",
            text: "STATUS",
            formatter: (cell) => (
                <div className="form-check form-switch mb-3" dir="ltr">
                    <input type="checkbox" className="form-check-input" id="customSwitch1" defaultChecked={cell === 1} />
                </div>
            )
        },
        {
            dataField: "action",
            text: "ACTION",
            formatter: () => (
                <>
                    <Button color="light" size="sm" className="me-1"><i className="far fa-edit"></i></Button>
                    <Button color="light" size="sm"><i className="far fa-times-circle"></i></Button>
                </>
            )
        }
    ]

    const leaveRequestColumns = [
        {
            dataField: "id",
            text: "#",
            formatter: (cell, row, rowIndex) => (pagination.page - 1) * pagination.limit + rowIndex + 1
        },
        { dataField: "leave_type_name", text: "TYPE" },
        { dataField: "from_date", text: "FROM" },
        { dataField: "to_date", text: "TO" },
        { dataField: "created_at", text: "REQUESTED DATE" },
        {
            dataField: "requested_by",
            text: "REQUESTED BY",
            formatter: (cell, row) => `${row.firstname || ""} ${row.lastname || ""}`
        },
        { dataField: "total_days", text: "REQUESTED DAYS" },
        { dataField: "reason", text: "REASON" },
        {
            dataField: "status",
            text: "STATUS",
            formatter: (cell) => (
                <span className={`badge ${cell === "Pending" ? "bg-warning" : "bg-success"}`}>{cell}</span>
            )
        }
    ]

    const customStyles = {
        control: (base) => ({
            ...base,
            height: 38,
            minHeight: 38
        })
    }

    const brownBtnStyle = { backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }
    const greenBtnStyle = { backgroundColor: "#159858", borderColor: "#159858", color: "#fff" }

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Dashboard | Leaves</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="HRMS" breadcrumbItem="Leaves" />

                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <Nav tabs className="nav-tabs-custom">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "2" })}
                                            onClick={() => toggleTab("2")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="d-block d-sm-none"><i className="far fa-user"></i></span>
                                            <span className="d-none d-sm-block">Leaves</span>
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "1" })}
                                            onClick={() => toggleTab("1")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="d-block d-sm-none"><i className="fas fa-cog"></i></span>
                                            <span className="d-none d-sm-block">Leave Types</span>
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                {activeTab === "1" ? (
                                    <Button style={brownBtnStyle} onClick={toggleLeaveTypeModal}>
                                        <i className="fas fa-plus me-1"></i> Add Leave Type
                                    </Button>
                                ) : (
                                    <Button style={brownBtnStyle} onClick={toggleLeaveRequestModal}>
                                        <i className="fas fa-plus me-1"></i> Create Leave Request
                                    </Button>
                                )}
                            </div>

                            <TabContent activeTab={activeTab} className="p-0 text-muted">
                                <TabPane tabId="1">
                                    <div className="text-uppercase mb-4" style={{ fontWeight: "bold", fontSize: "14px" }}>Leave Type Filter</div>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <Select
                                                placeholder="Select Branch"
                                                options={[]}
                                                styles={customStyles}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Input type="text" placeholder="Leave Type" />
                                        </Col>
                                        <Col md={4}>
                                            <Button style={greenBtnStyle} className="me-2">Filter</Button>
                                            <Button style={brownBtnStyle}>Reset</Button>
                                        </Col>
                                    </Row>

                                    <Datatables
                                        columns={leaveTypeColumns}
                                        rows={leaveTypeData.rows}
                                        keyField='id'
                                        rowsLength={leaveTypeData.total_rows}
                                        isTableHead={false}
                                        isAdd={false}
                                        isSearch={false}
                                        loading={isLoading}
                                        ssr={setPagination}
                                    />
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="text-uppercase mb-4" style={{ fontWeight: "bold", fontSize: "14px" }}>Leave Request Filter</div>
                                    <Row className="mb-4">
                                        <Col md={2}>
                                            <Select placeholder="Select Branch" options={[]} styles={customStyles} />
                                        </Col>
                                        <Col md={2}>
                                            <Select placeholder="Select Department" options={[]} styles={customStyles} />
                                        </Col>
                                        <Col md={2}>
                                            <Select placeholder="Select Employee" options={[]} styles={customStyles} />
                                        </Col>
                                        <Col md={2}>
                                            <Select placeholder="All Leave Type" options={[]} styles={customStyles} />
                                        </Col>
                                        <Col md={1}>
                                            <Input type="text" defaultValue="2026" />
                                        </Col>
                                        <Col md={1}>
                                            <Select placeholder="All Month" options={[]} styles={customStyles} />
                                        </Col>
                                        <Col md={1}>
                                            <Select placeholder="All Status" options={[]} styles={customStyles} />
                                        </Col>
                                        <Col md={1} className="d-flex align-items-end">
                                            <Button style={greenBtnStyle} className="me-1">Filter</Button>
                                            <Button style={brownBtnStyle}>Reset</Button>
                                        </Col>
                                    </Row>

                                    <Datatables
                                        columns={leaveRequestColumns}
                                        rows={leaveRequestData.rows}
                                        keyField="id"
                                        rowsLength={leaveRequestData.total_rows}
                                        isTableHead={false}
                                        isAdd={false}
                                        isSearch={false}
                                        loading={isLoading}
                                        ssr={setPagination}
                                    />
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* Leave Type Modal */}
            <Modal isOpen={leaveTypeModal} toggle={toggleLeaveTypeModal} size="xl">
                <ModalHeader toggle={toggleLeaveTypeModal}>Add Leave Type</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleLeaveTypeSubmit}>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Branch"
                                        options={[]}
                                        onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, branch_id: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Leave Type Name <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter Leave Type"
                                        value={leaveTypeForm.leave_type_name}
                                        onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, leave_type_name: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Applies to Gender</Label>
                                    <Select
                                        placeholder="Select Gender"
                                        options={[{ label: "Male", value: "male" }, { label: "Female", value: "female" }, { label: "Both", value: "Both" }]}
                                        onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, gender: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Is Paid Leave <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select"
                                        options={[{ label: "Yes", value: 1 }, { label: "No", value: 0 }]}
                                        onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, is_paid_leave: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Allocated Days <span className="text-danger">*</span></Label>
                                    <Input
                                        type="number"
                                        placeholder="Allocated Days"
                                        value={leaveTypeForm.leave_allocated_days}
                                        onChange={(e) => setLeaveTypeForm(prev => ({ ...prev, leave_allocated_days: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <div className="text-end mt-3">
                            <Button style={brownBtnStyle} type="submit">
                                <i className="fas fa-plus me-1"></i> Create Leave Type
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* Leave Request Modal */}
            <Modal isOpen={leaveRequestModal} toggle={toggleLeaveRequestModal} size="xl">
                <ModalHeader toggle={toggleLeaveRequestModal}>Create Leave Request</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleLeaveRequestSubmit}>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Branch"
                                        options={[]}
                                        onChange={(e) => setLeaveRequestForm(prev => ({ ...prev, branch_id: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Department <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Department"
                                        options={[]}
                                        onChange={(e) => setLeaveRequestForm(prev => ({ ...prev, department_id: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Lab Department <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Lab Department"
                                        options={[]}
                                        onChange={(e) => setLeaveRequestForm(prev => ({ ...prev, lab_department_id: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Requested For <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Employee"
                                        options={[]}
                                        onChange={(e) => setLeaveRequestForm(prev => ({ ...prev, employee_id: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Leave Type <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Leave Type"
                                        options={[]}
                                        onChange={(e) => setLeaveRequestForm(prev => ({ ...prev, leave_type_id: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>From Date <span className="text-danger">*</span></Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ dateFormat: "Y-m-d" }}
                                        onChange={(date, dateStr) => setLeaveRequestForm(prev => ({ ...prev, from_date: dateStr }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>To Date <span className="text-danger">*</span></Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ dateFormat: "Y-m-d" }}
                                        onChange={(date, dateStr) => setLeaveRequestForm(prev => ({ ...prev, to_date: dateStr }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>Reason <span className="text-danger">*</span></Label>
                                    <Input
                                        type="textarea"
                                        rows="4"
                                        value={leaveRequestForm.reason}
                                        onChange={(e) => setLeaveRequestForm(prev => ({ ...prev, reason: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <div className="mt-3">
                            <Button style={brownBtnStyle} type="submit">Submit</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

export default Leaves
